// @ts-check
import Helper from "Utils/Helper";
import AppStore from "Stores/AppStore";
import { ShowAlert } from "Utils/CommonFunctions";
import UtilityRequests from "Utils/Requests/UtilityRequests";

/**
 * @typedef {Object} FetchParam
 * @property {("POST"|"GET")} method
 * @property {string} url
 * @property {boolean} [skipError]
 * @property {any} [body]
 * @property {boolean} [bodyIsUrlParams]
 */

class SmrClient {
    /**
     * @param {FetchParam} params 
     * @returns 
     */
    async FetchJson(params) {
        let response = await this.FetchImpl(params);
        let retText = await response.text();
        return Helper.JsonParse(retText);
    }

    /**
     * @param {FetchParam} params
     */
    async FetchDownload(params) {
        /** @type {ValueResponse<PrepareDownloadFileTask>} */
        let resp = await this.FetchJson(params);
        if (resp.error)
            return;
        UtilityRequests.DownloadPreparedFile(resp.value.taskId);
    }

    /**
     * @param {FetchParam} params
     */
    Download(params) {
        let url = SmrClient.GetFullUrlFromRelative(params.url);

        // Использовать blob оказалось неверным - он грузится
        // полностью в память/диск прежде чем response.body() его вернёт.
        // Так что загрузка файлов теперь только такая старомодная, через a.href и form.
        // И у такого способа две основные проблемы - нет никакого контроля (нормального) над запросом.
        // Загрузка работает по принципу "запустил и забыл" - никакого ожидания ответа, никакой обработки ошибок.
        // Это нужно учитывать в дальнейшем.
        // Да, есть StreamSaver, который позволит грузить с обычным fetch, но там свои нюансы и хаки используются. В нашем случае, проще так.
        switch (params.method) {
            case "GET":
                if (params.body) {
                    url = url + '?' + this.CreateURLSearchParams(params.body).toString();
                }

                var a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;

                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                break;
            case "POST":
                const form = document.createElement('form');
                form.style.display = 'none';
                form.method = 'POST';
                form.action = url;

                if (params.body)
                    for (const key in params.body) {
                        if (params.body.hasOwnProperty(key)) {
                            const input = document.createElement('input');
                            input.type = 'hidden';
                            input.name = key;
                            input.value = params.body[key];
                            form.appendChild(input);
                        }
                    }

                document.body.appendChild(form);
                form.submit();
                document.body.removeChild(form);
                break;
        }
    }

    /**
     * @param {FetchParam} params
     * @private
     */
    async FetchImpl(params) {
        // todo перенести варианты параметров из ...\EOSAI\EOSAI\Front\Utils\SmrClient.js

        let fullUrl = SmrClient.GetFullUrlFromRelative(params.url);

        let body = null;
        if (params.body) {
            if (params.method == "GET") {
                fullUrl += '?' + this.CreateURLSearchParams(params.body).toString();
            }
            else {
                body = params.bodyIsUrlParams
                    ? this.CreateURLSearchParams(params.body).toString()
                    : JSON.stringify(params.body)
            }
        }

        let tryCount = 3;
        /** @type {Response | null} */
        let response = null;
        for (let tr = 1; tr <= tryCount; tr++) {
            try {
                response = await fetch(fullUrl, {
                    method: params.method,
                    body: body,
                    cache: "no-store",
                    headers: {
                        "Content-type": params.bodyIsUrlParams
                            ? "application/x-www-form-urlencoded; charset=utf-8"
                            : "application/json; charset=utf-8"
                    }
                });

                if (response.status > 0)
                    break;
            }
            catch (error) {
                if (tr == tryCount)
                    throw error;
            }

            await Helper.Delay(100);
        }

        if (response == null)
            throw new Error("Response is null");

        if (response.status == 200)
            return response;
        else if (response.status == 401
            || response.status == 403
            || response.status >= 500 && response.status < 600) {
            AppStore.SetLoggedInState(false);
            throw new Error(response.statusText);
        }
        else {
            this.AlertError(response, params);
            throw new Error(response.statusText);
        }
    }

    /**
     * @param {any} obj 
     * @returns {URLSearchParams}
     * @private
     */
    CreateURLSearchParams(obj) {
        let params = new URLSearchParams();

        Object.entries(obj)
            .forEach(kv => {
                // Если создавать через конструктор,
                // то все null эта фигня превращает в текст "null"
                // В итоге в бэк приходит текст вместо null или хотя пустой строки.
                if (kv[1] === null)
                    return;

                params.append(kv[0], kv[1]);
            });

        return params;
    }

    /**
     * @param {Response} response 
     * @param {FetchParam} params 
     * @private
     */
    AlertError(response, params) {
        console.log("Request to backend. Error. " + JSON.stringify({ skipError: params.skipError, status: response.status, statusText: response.statusText, url: params.url }));

        if (response.status === 405) // ..\DocMobileServer\Attributes\AuthorizeSmrAttribute.cs
            return;

        if (params.skipError == true)
            return;

        let errorText = `Ошибка HTTP: ${response.statusText} (${response.status})`;

        ShowAlert('error', errorText);
    }

    static GetRequestUrl() {
        return `${window.SmrLocation}/webUiApi`
    }

    /**
     * @param {string} smrRelativeUrl 
     * @returns {string}
     */
    static GetFullUrlFromRelative(smrRelativeUrl) {
        return SmrClient.GetRequestUrl() + (smrRelativeUrl.charAt(0) == "/" ? "" : "/") + smrRelativeUrl;
    }
}

export default new SmrClient;